// Pure derivation of the TAW archive scope (D-17/D-18) and the effective
// team scope, shared by App.js and this plan's tests. Kept dependency-free
// (imports nothing from src/App.js or src/store/) so a unit test can import
// this file alone without dragging the whole app module graph in.
//
// D-17: a team owner reviewing an agent's closed deals for a selected
// archive year is a real, deliberate deviation from Phase 40's D-03
// (active-only TAW windowing). D-18 is the SHAPE of that deviation: the
// archive year selector stays the single source of truth -- this scope only
// widens WHICH WORKSPACES that year covers, it never adds a second,
// independent year axis. One TAW slice per (taw, year), produced through
// useTawTransactions' single serializeScope call site -- never a
// locally-templated key here.
//
// WHY 'All years' degrades to the ACTIVE scope, never { kind: 'all' }:
// D-18 explicitly rejected "all archived years for the picked TAW" as
// unbounded and self-contradictory on screen (it would make the archive-year
// control and the TAW picker disagree about which year is showing). A
// { kind: 'all' } TAW scope would also open one unbounded LIVE listener per
// TAW (useTawTransactions.js's archive branch is a one-shot getDocs; only
// its 'active'-shaped branch opens onSnapshot), directly undoing Phase 40's
// COLD-02 boot-listener-count guarantee. The degraded scope is never
// actually queried for a team, because computeEffectiveTeamScope (below)
// pins the team scope to 'own' on that exact same path -- zero slices,
// zero reads. These two helpers are a matched pair and must always be used
// together; using one without the other reopens the hole D-18 closed.

export const ARCHIVE_YEAR_ALL = 'all';

export const TAW_ACTIVE_SCOPE = Object.freeze({ kind: 'active' });

/**
 * Derives the scope handed to useTawTransactions' fourth argument.
 *
 * Returns TAW_ACTIVE_SCOPE (by identity) unless the archive route is active
 * AND the archive year filter coerces to a finite number. The PillTabRail
 * year values are strings ('2024', 'all', ...) while buildConstraints'
 * archive case compares where('archiveYear', '==', scope.year) against a
 * stored NUMBER -- handing it a string would query a type no document holds
 * and return an empty set with NO error, which is exactly the silent-
 * failure class this phase exists to repair. Number(...) coercion here is
 * what prevents that.
 */
export function computeTawScope({ isArchiveView, archiveYearFilter }) {
    if (!isArchiveView) return TAW_ACTIVE_SCOPE;
    // Number('') coerces to 0 (finite!), not NaN -- an empty-string filter
    // must degrade to the active scope just like undefined/'nope' do, so
    // this is guarded explicitly rather than trusting Number.isFinite alone.
    if (archiveYearFilter === undefined || archiveYearFilter === null || archiveYearFilter === '') {
        return TAW_ACTIVE_SCOPE;
    }
    const year = Number(archiveYearFilter);
    if (!Number.isFinite(year)) return TAW_ACTIVE_SCOPE;
    return { kind: 'archive', year };
}

/**
 * Derives the team scope actually passed to useTawTransactions' third
 * argument. Identical to the caller-supplied teamScope in every case except
 * one: the archive route on 'All years' pins to 'own', so
 * useTawTransactions.computeActiveTawIds acquires ZERO TAW slices rather
 * than being handed a malformed/degraded archive scope. Task 3's visible
 * 'All years' notice is the UI half of this decision -- the two must ship
 * together so this zero-read path never presents as an unexplained empty
 * team archive.
 */
export function computeEffectiveTeamScope({ isArchiveView, archiveYearFilter, teamScope }) {
    if (isArchiveView && archiveYearFilter === ARCHIVE_YEAR_ALL) return 'own';
    return teamScope;
}
